import { Message } from "@/types/Message";
import { Talk } from "@/types/Talk";
import cloneDeep from "lodash/cloneDeep";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { transformMessageList } from "./message";
import { removeTyping } from "./message-process/removeTyping";
import { removeTempMsg } from "./message-process/removeTempMsg";
import { getLatestMsg } from "./message-process/getLatestMsg";
import { getSpokenFlag } from "./message-process/getSpokenFlag";
import { getOfflineFlag } from "./getOfflineFlag";
import { getNewCountAndWorkerReadFlag } from "./message-process/getNewCountAndWorkerReadFlag";
import { getWaitingFlag } from "./message-process/getWaitingFlag";
import { playNewMsgSound } from "./audio";
import { getAIReplyStatus, isAIPreview } from "./message-process/ai";
import { removeRecallMsg } from "./message-process/removeRecallMsg";

let cachedMessages: Message[] = [];
let cachedTalkListV4: Talk[] = [];

export function getPlatformType(rawPlatform: string) {
  if (rawPlatform === "pc" || rawPlatform === "mobile") {
    return "web";
  } else {
    return rawPlatform;
  }
}

export function sanitizeNewTalkList(oldList: Talk[], newList: Talk[]) {
  newList = cloneDeep(newList);

  let sanitizedList: Talk[] = [];

  // 必然存在的属性列表
  const definateProperties = [
    "uid",
    "microtime",
    "total",
    "name",
    "worker",
    "tags",
  ];

  // 可能存在的属性列表
  const possibleProperties = [
    {
      key: "nickname",
      defaultVal: "",
    },
    {
      key: "uaicons",
      defaultVal: [
        {
          icon: "unknown_os",
          text: "未知",
        },
        {
          icon: "unknown_brand",
          text: "未知",
        },
      ],
    },
    {
      key: "avatar",
      defaultVal: "",
    },
    {
      key: "referrer",
      defaultVal: null,
    },
    {
      key: "gender",
      defaultVal: 0,
    },
    {
      key: "wxappid",
    },
    {
      key: "appid",
    },
    {
      key: "weibo_uid",
    },
    {
      key: "ip",
    },
    {
      key: "isActive",
      defaultVal: false,
    },
    {
      key: "can_pm",
    },
    {
      key: "newestToReplyMsg",
    },
    {
      key: "toReplyMsg",
    },
    {
      key: "draft",
    },
    {
      key: "qrcode_name",
    },
    {
      key: "subPlatform",
    },
    {
      key: "note_info",
    },
    {
      key: "subscribe_from",
    },
  ];

  sanitizedList = newList
    .map((newTalk: Talk) => {
      const sanitizedTalk: any = {};
      const oldTalk = oldList.find((talk) => talk.uid === newTalk.uid);

      // 可以预先特殊处理的属性
      if (newTalk.platform) {
        sanitizedTalk.rawPlatform = newTalk.platform;
        sanitizedTalk.platform = getPlatformType(newTalk.platform);
      } else {
        if (!oldTalk) {
          return null;
        }
        sanitizedTalk.platform = oldTalk.platform;
        sanitizedTalk.rawPlatform = oldTalk.rawPlatform;
      }

      sanitizedTalk.from = newTalk.total;

      // conversation 预处理
      newTalk.conversation = newTalk.conversation.sort((a, b) => {
        return a.microtime - b.microtime;
      });

      newTalk.conversation.forEach((item: Message, index, arr) => {
        if (item.style && item.style === "hidden") {
          arr.splice(index, 1);
        }
      });

      newTalk.conversation = 
        transformMessageList(
          newTalk.conversation,
          sanitizedTalk.platform,
          newTalk.uid
        )
      ;

      // 更新广告消息
      newTalk.conversation.forEach((msg) => {
        if (msg.type === "ref_ad") {
          newTalk.subscribe_ad = msg.ext;
        }
        if (msg.type === "ref_note") {
          newTalk.note_info = msg.ext;
        }
      });

      //要获取最后一条刷新消息
      const reversed = [...newTalk.conversation].reverse();
      const refreshMsg = reversed.find((m: any) =>
        [
          "refresh_user_info",
          "refresh_wechat_user_info",
          "user_authorization_revoke",
        ].includes(m.type)
      );
      if (refreshMsg) {
        newTalk = {
          ...newTalk,
          ...refreshMsg?.ext,
        };
      }

      sanitizedTalk.shouldRefreshInfoFlag = newTalk.conversation.some(
        (msg: Message) => {
          return (
            ["tags_add", "tags_remove", "lead_edit"].includes(msg.feType) ||
            msg.type === "refresh_user_info"
          );
        }
      );

      sanitizedTalk.conversation = oldTalk
        ? oldTalk.conversation.concat(newTalk.conversation)
        : newTalk.conversation;

      sanitizedTalk.conversation = sanitizedTalk.conversation.sort(
        (a: any, b: any) => a.microtime - b.microtime
      );

      sanitizedTalk.conversation = uniqBy(
        sanitizedTalk.conversation,
        (msg: Message) =>
          msg.originalMicrotime != null
            ? `${msg.microtime}_${msg.originalMicrotime}`
            : msg.microtime
      );

      sanitizedTalk.conversation = removeRecallMsg(
        removeTyping(removeTempMsg(sanitizedTalk.conversation))
      );

      // 直接覆盖的属性
      definateProperties.forEach((key: string) => {
        sanitizedTalk[key] = newTalk[key];
      });

      // 选填属性
      possibleProperties.forEach(({ key, defaultVal }) => {
        if (typeof newTalk[key] !== "undefined") {
          sanitizedTalk[key] = newTalk[key];
        } else if (oldTalk && oldTalk[key]) {
          sanitizedTalk[key] = oldTalk[key];
        } else if (typeof defaultVal !== "undefined") {
          sanitizedTalk[key] = defaultVal;
        }
      });

      // 继续特殊处理属性

      // ai状态
      if (oldTalk?.isAIPreview) {
        sanitizedTalk.isAIPreview = true;
      }

      //默认值
      sanitizedTalk.showAISwitch = false;
      if (
        ["wechat", "douyin", "web", "kuaishou", "redbook"].includes(
          sanitizedTalk.platform
        )
      ) {
        const newIsAIPreview = isAIPreview(newTalk);
        if (newIsAIPreview) {
          sanitizedTalk.isAIPreview = true;
        }

        const newAiStatus = getAIReplyStatus(
          newTalk,
          sanitizedTalk.isAIPreview
        );

        if (newAiStatus === "void") {
          if (!oldTalk) {
            sanitizedTalk.showAISwitch = false;
          } else {
            sanitizedTalk.showAISwitch = oldTalk.showAISwitch;
            sanitizedTalk.aiStatus = oldTalk.aiStatus;
          }
        } else if (newAiStatus === "closed") {
          sanitizedTalk.showAISwitch = false;
        } else {
          sanitizedTalk.showAISwitch = true;
          sanitizedTalk.aiStatus = newAiStatus;
        }
      }

      //网页渠道，关键词后续刷新
      const refreshMsgWeb = newTalk.conversation.find(
        (m) => m.cmd === "refresh_user_info"
      );
      if (refreshMsgWeb) {
        if (!sanitizedTalk.referrer) sanitizedTalk.referrer = {};
        sanitizedTalk.referrer = {
          ...sanitizedTalk.referrer,
          keyword: refreshMsgWeb.body?.keyword || "",
        };
      }

      // 网页地址直辖市可能有重复
      if (newTalk.iplocation) {
        ["北京", "上海", "重庆", "天津", "保留地址\t保留地址"].forEach(
          (item) => {
            newTalk.iplocation = newTalk.iplocation!.replace(item, "");
          }
        );
      }

      let newFeLocation =
        newTalk.wxlocation || newTalk.iplocation || newTalk.location;

      if (newFeLocation) {
        newFeLocation = newFeLocation
          .replace(/\s/g, "")
          .replace("中国", "")
          .replace("上海上海", "上海")
          .replace("北京北京", "北京")
          .replace("天津天津", "天津")
          .replace("重庆重庆", "重庆");
        sanitizedTalk.feLocation = newFeLocation;
      } else if (oldTalk && oldTalk.feLocation) {
        sanitizedTalk.feLocation = oldTalk.feLocation;
      }

      // 微信广告来源
      if (newTalk.subscribe_ad) {
        sanitizedTalk.subscribe_ad = newTalk.subscribe_ad;
        if (newTalk.subscribe_ad.subscribe_from) {
          sanitizedTalk.subscribe_from = newTalk.subscribe_ad.subscribe_from;
        }

        if (sanitizedTalk.subscribe_ad?.campaign_name) {
          sanitizedTalk.subscribe_adText =
            sanitizedTalk.subscribe_ad.campaign_name;
          sanitizedTalk.subscribe_adTextType = "广告计划";
        } else if (sanitizedTalk.subscribe_ad?.adgroup_name) {
          sanitizedTalk.subscribe_adText =
            sanitizedTalk.subscribe_ad.adgroup_name;
          sanitizedTalk.subscribe_adTextType = "广告";
        } else if (sanitizedTalk.subscribe_ad?.campaign_id) {
          sanitizedTalk.subscribe_adText = `ID(${sanitizedTalk.subscribe_ad.campaign_id})`;
          sanitizedTalk.subscribe_adTextType = "广告计划";
        } else if (sanitizedTalk.subscribe_ad?.adgroup_id) {
          sanitizedTalk.subscribe_adText = `ID(${sanitizedTalk.subscribe_ad.adgroup_id})`;
          sanitizedTalk.subscribe_adTextType = "广告";
        } else {
          sanitizedTalk.subscribe_adText = "";
        }
      } else if (oldTalk && oldTalk.subscribe_ad) {
        sanitizedTalk.subscribe_ad = oldTalk.subscribe_ad;
        sanitizedTalk.subscribe_adText = oldTalk.subscribe_adText;
      }
      if (
        sanitizedTalk.subscribe_from === "扫描二维码" &&
        sanitizedTalk.qrcode_name
      ) {
        sanitizedTalk.subscribe_from = "渠道二维码";
      }

      // 非微信广告来源
      if (
        newTalk.subscribe_ad &&
        ["douyin", "kuaishou", "redbook", "web"].includes(
          sanitizedTalk.platform
        )
      ) {
        sanitizedTalk.subscribe_ad = newTalk.subscribe_ad;
        if (sanitizedTalk.subscribe_ad.campaign_name) {
          sanitizedTalk.subscribe_adText =
            sanitizedTalk.subscribe_ad.campaign_name;
        } else if (sanitizedTalk.subscribe_ad.campaign_id) {
          sanitizedTalk.subscribe_adText = `ID(${sanitizedTalk.subscribe_ad.campaign_id})`;
        } else {
          sanitizedTalk.subscribe_adText = "";
        }
      } else if (oldTalk && oldTalk.subscribe_ad) {
        sanitizedTalk.subscribe_ad = oldTalk.subscribe_ad;
        sanitizedTalk.subscribe_adText = oldTalk.subscribe_adText;
      }

      // 小红书渠道 笔记信息
      if (sanitizedTalk.platform === "redbook") {
        if (newTalk.note_info) {
          sanitizedTalk.subscribe_ad = {
            ...(sanitizedTalk.subscribe_ad || {}),
            note_info: newTalk.note_info,
          };
        }
      }

      if (sanitizedTalk.wxappid) {
        sanitizedTalk.appid = sanitizedTalk.wxappid;
      }

      sanitizedTalk.latestMsgText = getLatestMsg(sanitizedTalk);
      sanitizedTalk.spokenFlag = getSpokenFlag(sanitizedTalk);
      sanitizedTalk.offlineFlag = getOfflineFlag(sanitizedTalk);

      const waitingFlag = getWaitingFlag(sanitizedTalk);
      sanitizedTalk.waitingFlag = waitingFlag;

      // newCount计算
      const [newCount, workerReadFlag]: any =
        getNewCountAndWorkerReadFlag(newTalk);

      if (oldTalk && oldTalk.newCount && !workerReadFlag) {
        sanitizedTalk.newCount = oldTalk.newCount + newCount;
      } else {
        sanitizedTalk.newCount = newCount;
      }
      sanitizedTalk.hasNewMsgFlag = !!newCount && !sanitizedTalk.isHosted;

      if (sanitizedTalk.isActive && document.hasFocus()) {
        sanitizedTalk.newCount = 0;
      }

      if (!sanitizedTalk.microtime) {
        const isHumanMessages = sanitizedTalk.conversation.filter(
          (msg: any) => !!msg.is_human
        );
        sanitizedTalk.microtime = isHumanMessages?.length
          ? isHumanMessages[isHumanMessages.length - 1].microtime
          : 0;
      }

      return sanitizedTalk as Talk;
    })
    .filter((talk) => {
      return !!talk;
    }) as Talk[];

  sanitizedList = orderBy(
    sanitizedList,
    ["waitingFlag", "offlineFlag", "spokenFlag", "microtime"],
    ["desc", "asc", "desc", "desc"]
  );

  handleTalkList(sanitizedList);

  return sanitizedList;
}

function handleTalkList(talkList: Talk[]) {
  let totalNewCount = 0;
  let hasNewMsgFlag = false;
  talkList.forEach((talk: any) => {
    if (talk.hasNewMsgFlag) {
      hasNewMsgFlag = true;
    }
    totalNewCount += talk.newCount;
  });

  if (hasNewMsgFlag) {
    // this.audioService.playNewMessage();
    playNewMsgSound();
  }
}

export function transformTalkListToFe(talkList: any): Talk[] {
  talkList = cloneDeep(talkList);
  return Object.entries(talkList).map(([uid, val]: [string, any]) => {
    val.uid = uid;
    return { ...val };
  });
}

export function transformTalkListToFeV3(
  talkList: any,
  messages: Message[]
): Talk[] {
  talkList = cloneDeep(talkList);

  const uidList = Object.entries(talkList).map(([uid]) => uid);
  cachedMessages.forEach((msg) => {
    if (uidList.includes(msg.uid!)) {
      messages.push(msg);
    }
  });
  cachedMessages = cachedMessages.filter((msg) => !uidList.includes(msg.uid!));

  messages.forEach((msg) => {
    if (!uidList.includes(msg.uid!)) {
      cachedMessages.push(msg);
    }
  });

  return Object.entries(talkList).map(([uid, val]: [string, any]) => {
    return {
      ...val,
      uid,
      conversation: messages.filter((msg) => msg.uid === uid),
    };
  });
}

export function v4CraftTalkList(
  uids: string[],
  messages: Message[],
  exisitingTalkList: Talk[]
) {
  const talkList = uids.map((uid) => {
    // 从新往旧排序
    const currentTalkMessages = messages
      .filter((msg) => msg.uid === uid)
      .sort((a, b) => b.microtime - a.microtime);

    const exisitingTalk = exisitingTalkList.find((talk) => talk.uid === uid);
    const exisitingTalkMessages = exisitingTalk?.conversation || [];
    const cachedTalk = cachedTalkListV4.find((talk) => talk.uid === uid && talk.worker);

    const metaMessages = [
      ...currentTalkMessages,
      ...[...exisitingTalkMessages].reverse(),
    ].filter((msg: any) => {
      return [
        "visitor_add",
        "visitor_remove",
        "tags_add",
        "tags_remove",
        "ref_user_avatar",
        "edit_user",
      ].includes(msg.type);
    });

    const visitorAddMsg: any = metaMessages.find(
      (msg) => msg.type === "visitor_add"
    );

    //talkinfo 基础信息，如果存在cachedTalk, 则不再读取 visitor_add,避免污染
      // 比如 subscribe_ad 信息，visitor_add 中获取
      // 后续通过ref_ad 更改以后，这里如果再读 visitor_add 的，会覆盖 ref_ad中的值
    const talkInfo = {
      ...(cachedTalk || visitorAddMsg?.ext || {}),
    };

    const visitorRemoveMsg = metaMessages.find(
      (msg) => msg.type === "visitor_remove"
    );

    let workerInfo = {};
    if (visitorRemoveMsg) {
      workerInfo = {
        worker: visitorRemoveMsg?.ext?.worker,
      };
    }

    let tagInfo = {};
    const tagsOperateMsg = metaMessages.find((msg: any) =>
      ["tags_add", "tags_remove"].includes(msg.type)
    );
    if (tagsOperateMsg) {
      tagInfo = {
        tags: tagsOperateMsg.ext.visitor_tags || [],
      };
    }

    const refUserAvatarMsg = metaMessages.find(
      (msg) => msg.type === "ref_user_avatar"
    ) as (Message & { avatar: string }) | undefined;
    if (refUserAvatarMsg) {
      talkInfo.avatar = refUserAvatarMsg.avatar;
    }

    const editUserMsgs = metaMessages.filter(
      (msg) => msg.type === "edit_user"
    ) as any;
    if (editUserMsgs?.length) {
      editUserMsgs.reverse().forEach((msg: any) => {
        if (msg.ext.modified_fields?.name) {
          talkInfo.name = msg.ext.modified_fields.name;
        }
      });
    }

    return {
      uid,
      ...talkInfo,
      conversation: currentTalkMessages,
      ...workerInfo,
      ...tagInfo,
    };
  });
  cachedTalkListV4 = cloneDeep(talkList).map((item) => {
    // 避免subscribe_ad note_info 污染
    delete item?.note_info;
    delete item?.subscribe_ad;
    delete item?.conversation;

    delete item?.avatar;
    delete item?.name;

    return item;
  });
  return talkList;
}
